import { useState } from 'react'
import type {
  EmailTemplate,
  PipelineOutcome,
  PipelineStage,
  TemplateKey,
} from '@/types/crm'
import { EMPTY_TEMPLATE } from '../defaults'

interface Props {
  stages: PipelineStage[]
  outcomes: PipelineOutcome[]
  templates: Record<TemplateKey, EmailTemplate>
  onSave: (key: TemplateKey, template: EmailTemplate) => Promise<void>
  readOnly: boolean
}

interface TabDef {
  key: TemplateKey
  label: string
  color: string
  bg: string
}

/**
 * Bilingual template editor.
 *
 * Prospect/Lead splits into two tabs — the awareness variants the workspace
 * picks between automatically. Every other stage and both outcomes get one tab.
 */
function buildTabs(stages: PipelineStage[], outcomes: PipelineOutcome[]): TabDef[] {
  const tabs: TabDef[] = []
  for (const s of stages) {
    if (s.key === 'prospect') {
      tabs.push({ key: 'prospect_aware', label: `${s.label} — knows Skida`, color: s.color, bg: s.bg })
      tabs.push({
        key: 'prospect_unaware',
        label: `${s.label} — new to Skida`,
        color: s.color,
        bg: s.bg,
      })
    } else {
      tabs.push({ key: s.key, label: s.label, color: s.color, bg: s.bg })
    }
  }
  for (const o of outcomes) {
    tabs.push({ key: o.key, label: o.label, color: o.color, bg: o.bg })
  }
  return tabs
}

export function TemplateEditor({ stages, outcomes, templates, onSave, readOnly }: Props) {
  const tabs = buildTabs(stages, outcomes)
  const [activeKey, setActiveKey] = useState<TemplateKey>(tabs[0]?.key ?? 'prospect_aware')
  const [draft, setDraft] = useState<EmailTemplate | null>(null)
  const [saving, setSaving] = useState(false)

  const stored = templates[activeKey] ?? EMPTY_TEMPLATE
  const current = draft ?? stored
  const dirty = draft !== null

  const selectTab = (key: TemplateKey) => {
    setActiveKey(key)
    setDraft(null)
  }

  const patch = (fields: Partial<EmailTemplate>) => setDraft({ ...current, ...fields })

  const save = async () => {
    if (!draft) return
    setSaving(true)
    try {
      await onSave(activeKey, draft)
      setDraft(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admincard">
      <div className="card-label">Email templates</div>

      <div className="tmpl__tabs">
        {tabs.map((t) => {
          const active = t.key === activeKey
          return (
            <button
              key={t.key}
              className="tmpl__tab"
              onClick={() => selectTab(t.key)}
              style={{
                background: active ? t.bg : '#fff',
                color: active ? t.color : 'var(--color-text-muted)',
                borderColor: active ? t.color : 'var(--color-border)',
              }}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      <label className="tmpl__field">
        <span className="tmpl__legend">Internal guidance</span>
        <textarea
          className="textarea"
          rows={2}
          value={current.guidance}
          onChange={(e) => patch({ guidance: e.target.value })}
          disabled={readOnly}
        />
      </label>

      <label className="tmpl__field">
        <span className="tmpl__legend">Card title</span>
        <input
          className="input"
          value={current.title}
          onChange={(e) => patch({ title: e.target.value })}
          disabled={readOnly}
        />
      </label>

      <div className="tmpl__langs">
        <div>
          <div className="tmpl__lang tmpl__lang--no">🇳🇴 Norsk</div>
          <label className="tmpl__sublabel">Subject</label>
          <input
            className="input tmpl__subject"
            value={current.subjectNo}
            onChange={(e) => patch({ subjectNo: e.target.value })}
            disabled={readOnly}
          />
          <label className="tmpl__sublabel">Body</label>
          <textarea
            className="textarea tmpl__body"
            rows={9}
            value={current.bodyNo}
            onChange={(e) => patch({ bodyNo: e.target.value })}
            disabled={readOnly}
          />
        </div>
        <div>
          <div className="tmpl__lang tmpl__lang--en">🇬🇧 English</div>
          <label className="tmpl__sublabel">Subject</label>
          <input
            className="input tmpl__subject"
            value={current.subjectEn}
            onChange={(e) => patch({ subjectEn: e.target.value })}
            disabled={readOnly}
          />
          <label className="tmpl__sublabel">Body</label>
          <textarea
            className="textarea tmpl__body"
            rows={9}
            value={current.bodyEn}
            onChange={(e) => patch({ bodyEn: e.target.value })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="micro admincard__note">
        Saved edits apply immediately to the “How to push this deal forward” card on every lead in
        this stage.
      </div>

      {!readOnly && (
        <div className="admincard__actions">
          <button className="btn btn--primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save template' : 'Saved'}
          </button>
          {dirty && (
            <button className="btn btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
              Discard
            </button>
          )}
        </div>
      )}
    </div>
  )
}
